// File: packages/components/src/homepage/how-it-works/HowItWorksSection.tsx
import React from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Button } from '@dopomogai/ui';
import {
  Bot, Wand2, UserSearch, Calculator, Rocket, ClipboardList, CheckCircle2, Gauge, PlayCircle
} from 'lucide-react';

type Accent = 'violet' | 'cyan' | 'blue' | 'emerald' | 'slate' | 'amber' | 'rose' | 'indigo' | 'purple' | 'teal' | 'orange' | 'lime' | 'fuchsia' | 'pink' | 'gray';

type LucideIconName =
  | 'bot' | 'wand-2' | 'user-search' | 'calculator' | 'rocket'
  | 'clipboard-list' | 'check-circle-2' | 'gauge' | 'play-circle';

const iconFromName = (name?: LucideIconName, accent?: Accent) => {
  const color =
    accent === 'violet' ? 'text-violet-700' :
    accent === 'cyan' ? 'text-cyan-700' :
    accent === 'blue' ? 'text-blue-700' :
    accent === 'emerald' ? 'text-emerald-700' :
    accent === 'amber' ? 'text-amber-700' :
    accent === 'rose' ? 'text-rose-700' :
    accent === 'indigo' ? 'text-indigo-700' :
    accent === 'purple' ? 'text-purple-700' :
    accent === 'teal' ? 'text-teal-700' :
    accent === 'orange' ? 'text-orange-700' :
    accent === 'lime' ? 'text-lime-700' :
    accent === 'fuchsia' ? 'text-fuchsia-700' :
    accent === 'pink' ? 'text-pink-700' :
    accent === 'slate' ? 'text-slate-700' : 'text-slate-700';
  const common = { size: 22, className: color };
  switch (name) {
    case 'bot': return <Bot {...common} />;
    case 'wand-2': return <Wand2 {...common} />;
    case 'user-search': return <UserSearch {...common} />;
    case 'calculator': return <Calculator {...common} />;
    case 'rocket': return <Rocket {...common} />;
    case 'clipboard-list': return <ClipboardList {...common} />;
    case 'check-circle-2': return <CheckCircle2 {...common} />;
    case 'gauge': return <Gauge {...common} />;
    case 'play-circle': return <PlayCircle {...common} />;
    default: return null;
  }
};

const stripeClass = (accent?: Accent) => {
  switch (accent) {
    case 'violet': return 'bg-violet-400';
    case 'cyan': return 'bg-cyan-400';
    case 'blue': return 'bg-blue-400';
    case 'emerald': return 'bg-emerald-400';
    case 'amber': return 'bg-amber-400';
    case 'rose': return 'bg-rose-400';
    case 'indigo': return 'bg-indigo-400';
    case 'purple': return 'bg-purple-400';
    case 'teal': return 'bg-teal-400';
    case 'orange': return 'bg-orange-400';
    case 'lime': return 'bg-lime-400';
    case 'fuchsia': return 'bg-fuchsia-400';
    case 'pink': return 'bg-pink-400';
    case 'slate': return 'bg-slate-400';
    default: return 'bg-slate-300';
  }
};

export type HowStep = {
  title?: string;
  titleKey?: string;
  text?: string;
  textKey?: string;
  icon?: React.ReactNode;
  iconName?: LucideIconName;
  accent?: Accent;                // NEW
  bullets?: string[];             // NEW (optional sub-points)
  cta?: { label: string; actionId: string }; // NEW (per-step CTA)
};

export type HowItWorksSectionProps = {
  title?: string;
  titleKey?: string;
  subtitle?: string;
  subtitleKey?: string;
  steps: HowStep[];

  primaryCta?: { label?: string; labelKey?: string; actionId: string };
  secondaryCtas?: { label?: string; labelKey?: string; actionId: string }[];

  onAction?: (actionId: string, payload?: any) => void;

  columns?: 2 | 3 | 4;
  className?: string;
};

export const HowItWorksSection: React.FC<HowItWorksSectionProps> = ({
  title,
  titleKey,
  subtitle,
  subtitleKey,
  steps,
  primaryCta,
  secondaryCtas = [],
  onAction,
  columns = 4,
  className,
}) => {
  const { t } = useTranslation('homepage');

  const resolvedTitle = titleKey ? t(titleKey) : title;
  const resolvedSubtitle = subtitleKey ? t(subtitleKey) : subtitle;

  const gridCols =
    columns === 4
      ? 'md:grid-cols-2 lg:grid-cols-4'
      : columns === 3
      ? 'md:grid-cols-2 lg:grid-cols-3'
      : 'md:grid-cols-2';

  return (
    <section className={clsx('bg-slate-50', className)}>
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-12 md:py-16">
        {(resolvedTitle || resolvedSubtitle) && (
          <div className="mb-8 md:mb-10">
            {resolvedTitle && (
              <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900">{resolvedTitle}</h2>
            )}
            {resolvedSubtitle && (
              <p className="mt-3 text-slate-700 text-base md:text-lg max-w-3xl">{resolvedSubtitle}</p>
            )}
          </div>
        )}

        <div className={clsx('grid gap-5 md:gap-6', gridCols)}>
          {steps.map((s, idx) => {
            const stepTitle = s.titleKey ? t(s.titleKey) : (s.title ?? '');
            const stepText = s.textKey ? t(s.textKey) : (s.text ?? '');
            const iconNode = s.icon ?? iconFromName(s.iconName, s.accent);
            const stripe = stripeClass(s.accent);

            return (
              <div
                key={idx}
                className={clsx(
                  'relative bg-white border-2 border-black rounded-xl p-5 md:p-6',
                  'transition-all duration-200 hover:-translate-x-1 hover:-translate-y-1 hover:shadow-[4px_4px_0_0_rgb(17,24,39)]'
                )}
              >
                <div aria-hidden="true" className={clsx('absolute inset-x-2 top-2 h-2 rounded-full', stripe)} />

                <div className="flex items-center gap-2">
                  <div className="inline-flex items-center justify-center w-7 h-7 rounded-full border-2 border-black bg-white text-xs font-extrabold text-slate-900">
                    {idx + 1}
                  </div>
                  {iconNode}
                </div>

                <h3 className="mt-3 text-lg md:text-xl font-extrabold text-slate-900">{stepTitle}</h3>
                {stepText ? <p className="mt-2 text-slate-700">{stepText}</p> : null}

                {s.bullets?.length ? (
                  <ul className="mt-2 list-disc list-inside text-slate-700 space-y-1">
                    {s.bullets.map((b, i) => <li key={i}>{b}</li>)}
                  </ul>
                ) : null}

                {s.cta ? (
                  <div className="mt-4">
                    <Button
                      variant="secondary"
                      text={s.cta.label}
                      onClick={() => onAction?.(s.cta!.actionId, { step: idx + 1 })}
                    />
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>

        {(primaryCta || secondaryCtas.length > 0) && (
          <div className="mt-8 md:mt-10 flex flex-wrap gap-3">
            {primaryCta ? (
              <Button
                variant="primary"
                text={primaryCta.labelKey ? t(primaryCta.labelKey) : (primaryCta.label ?? 'Get started')}
                onClick={() => onAction?.(primaryCta.actionId)}
              />
            ) : null}
            {secondaryCtas.map((c, i) => (
              <Button
                key={i}
                variant="secondary"
                text={c.labelKey ? t(c.labelKey) : (c.label ?? 'Learn more')}
                onClick={() => onAction?.(c.actionId)}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default HowItWorksSection;
